import {
  Form,
  Link,
  redirect,
  useActionData,
  useNavigation,
  useSearchParams,
  type ActionFunctionArgs,
} from "react-router";
import { ApiError } from "@repo/api-client";
import { useSuspenseQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queries } from "@/lib/queries";
import { forgetEverything, read } from "@/lib/query";
import { errorMessage, loadSession, safeNext } from "@/lib/session";
import {
  AuthCard,
  ErrorNote,
  Field,
  OAUTH_ERRORS,
  PasswordField,
  ProviderButtons,
  SubmitButton,
} from "@/components/form";
import { CircleCheck, MailCheck } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

export async function signInLoader({ request }: { request: Request }) {
  // Already signed in: skip the form rather than showing it to someone who
  // does not need it.
  const [session] = await Promise.all([loadSession(), read(queries.providers())]);
  if (session) {
    const next = new URL(request.url).searchParams.get("next");
    throw redirect(safeNext(next));
  }
  return null;
}

type SignInResult = {
  error?: string;
  email?: string;
  /**
   * The password was right, but the address was never verified — only an
   * account from before email-first sign-up can be here. A link to the
   * address lets whoever holds it choose a password, which settles it.
   */
  unverified?: boolean;
  linkSent?: boolean;
};

export async function signInAction({ request }: ActionFunctionArgs): Promise<SignInResult | Response> {
  const form = await request.formData();
  const email = String(form.get("email") ?? "").trim();

  if (String(form.get("intent")) === "link") {
    try {
      await api.auth.signUp({ email });
    } catch (error) {
      return { error: errorMessage(error, "Could not send the link"), email, unverified: true };
    }
    return { email, linkSent: true };
  }

  try {
    await api.auth.signIn({ email, password: String(form.get("password") ?? "") });
  } catch (error) {
    const unverified = error instanceof ApiError && error.status === 403;
    return { error: errorMessage(error, "Could not sign in"), email, unverified };
  }

  // The cookie is set; every loader must now ask again rather than reuse
  // anything read signed out, or as somebody else.
  forgetEverything();
  return redirect(safeNext(form.get("next")));
}

export default function SignIn() {
  const { data: providers } = useSuspenseQuery(queries.providers());
  const result = useActionData() as SignInResult | undefined;
  const navigation = useNavigation();
  const [searchParams] = useSearchParams();

  const next = safeNext(searchParams.get("next"));
  const oauthError = searchParams.get("error");
  const verified = searchParams.get("verified") === "1";
  const submitting = navigation.state === "submitting";
  // Somebody sent here on the way to an invitation, say, who has no account
  // yet, keeps where they were going through sign-up and its email.
  const signUpHref = next === "/" ? "/sign-up" : `/sign-up?next=${encodeURIComponent(next)}`;

  return (
    <AuthCard
      title="Sign in"
      footer={
        <>
          No account?{" "}
          <Link to={signUpHref} className="font-medium underline underline-offset-4">
            Sign up
          </Link>
        </>
      }
    >
      {/* Good news, not an alarm: a status, which a screen reader reads
          when it comes rather than breaking in. */}
      {verified && (
        <Alert role="status" className="border-transparent bg-success-muted text-success">
          <CircleCheck />
          <AlertDescription className="text-foreground">Your email is verified. Sign in to continue.</AlertDescription>
        </Alert>
      )}
      {oauthError && <ErrorNote message={OAUTH_ERRORS[oauthError] ?? OAUTH_ERRORS.server_error!} />}
      {result?.linkSent && (
        <Alert role="status" className="border-transparent bg-success-muted text-success">
          <MailCheck />
          <AlertDescription className="text-foreground">
            We sent a link to <strong className="font-medium">{result.email}</strong>. Open it to choose your
            password.
          </AlertDescription>
        </Alert>
      )}
      {result?.error && <ErrorNote message={result.error} />}
      {result?.unverified && (
        <Form method="post">
          <input type="hidden" name="intent" value="link" />
          <input type="hidden" name="email" value={result.email} />
          {/* Too long for one line on a phone: it wraps, centred, rather than
              pushing the card wider than the screen. */}
          <Button
            type="submit"
            variant="outline"
            size="lg"
            disabled={submitting}
            className="h-auto min-h-10 w-full py-2 text-center whitespace-normal"
          >
            Email me a link to choose a password
          </Button>
        </Form>
      )}

      <Form method="post" replace className="space-y-4">
        <input type="hidden" name="next" value={next} />
        <Field
          label="Email"
          name="email"
          type="email"
          // "username", not "email": it is what this account signs in with,
          // so a password manager saves and fills it as the login.
          autoComplete="username"
          defaultValue={result?.email}
        />
        <PasswordField label="Password" name="password" autoComplete="current-password" checkLength={false} />
        <div className="-mt-2 text-right">
          <Link to="/forgot-password" className="text-sm font-medium underline underline-offset-4">
            Forgot password?
          </Link>
        </div>
        <SubmitButton pending={submitting}>{submitting ? "Signing in…" : "Sign in"}</SubmitButton>
      </Form>

      <ProviderButtons providers={providers} next={next} />
    </AuthCard>
  );
}
